import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import "./JobDetail.scss";
import PageBanner from "../layouts/PageBanner";
import aboutContact from "../../assets/about-hero.jpg";

const API = "https://api.ainfotech.com.np";

/* ---------------------- helpers (module scope) ---------------------- */

function decodeEntities(html = "") {
  if (!html) return "";
  return /&lt;\/?[a-z]/i.test(html)
    ? (() => {
        const t = document.createElement("textarea");
        t.innerHTML = html;
        return t.value;
      })()
    : html;
}

function sanitizeHTML(raw = "") {
  const decoded = decodeEntities(raw);
  return DOMPurify.sanitize(decoded, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder"],
    ADD_TAGS: ["iframe"],
  });
}

function buildJobHtml(job) {
  const direct =
    job.content_html ||
    job.body_html ||
    job.html ||
    job.description_html ||
    "";

  if (direct) return sanitizeHTML(direct);

  const parts = [];
  const add = (heading, html) => {
    if (!html) return;
    if (heading) parts.push(`<h3>${heading}</h3>`);
    parts.push(html);
  };

  const sections =
    job.sections_html && typeof job.sections_html === "object"
      ? job.sections_html
      : null;

  if (sections) {
    const preferredOrder = [
      "overview",
      "summary",
      "about",
      "intro",
      "description",
      "details",
      "jd",
      "skills",
      "requirements",
      "qualifications",
    ];

    const orderedKeys = [
      ...preferredOrder.filter((k) => sections[k]),
      ...Object.keys(sections).filter((k) => !preferredOrder.includes(k)),
    ];

    orderedKeys.forEach((k) => {
      const headingMap = {
        overview: "Overview",
        summary: "Overview",
        about: "Overview",
        intro: "Overview",
        description: "Job Description",
        details: "Job Description",
        jd: "Job Description",
        skills: "Required Skills",
        requirements: "Required Skills",
        qualifications: "Required Skills",
      };
      add(headingMap[k] || null, sections[k]);
    });

    return sanitizeHTML(parts.join("\n") || "<p></p>");
  }

  add("Overview", job.overview_html || job.summary_html || "");
  add("Job Description", job.description_html || job.details_html || job.jd_html || "");
  add("Required Skills", job.skills_html || job.requirements_html || job.qualifications_html || "");

  return sanitizeHTML(parts.join("\n") || "<p></p>");
}

/* ----------------------------- Toast UI ----------------------------- */

const Toast = ({ show, variant = "success", title, message, onClose, duration = 5000 }) => {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => onClose?.(), duration);
    return () => clearTimeout(t);
  }, [show, duration, onClose]);

  return (
    <div
      className={`avena-toast avena-toast--${variant} ${show ? "is-visible" : ""}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="avena-toast__icon">
        {variant === "success" && <i className="bi bi-check-circle" aria-hidden="true" />}
        {variant === "error" && <i className="bi bi-x-circle" aria-hidden="true" />}
        {variant === "info" && <i className="bi bi-info-circle" aria-hidden="true" />}
      </div>
      <div className="avena-toast__content">
        <div className="avena-toast__title">{title}</div>
        {message && <div className="avena-toast__message">{message}</div>}
      </div>
      <button className="avena-toast__close" onClick={onClose} aria-label="Close notification">
        <i className="bi bi-x" aria-hidden="true" />
      </button>
      <div className="avena-toast__progress" />
    </div>
  );
};

/* ----------------------------- component ---------------------------- */

export default function JobDetail() {
  const { id } = useParams(); // slug
  const [job, setJob] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API}/api/careers/${id}`, { signal: ac.signal });
        if (ac.signal.aborted) return;
        if (res.status === 404) { setJob(null); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load job");
        setJob(data);
        window.scrollTo(0, 0);
      } catch (e) {
        if (!ac.signal.aborted) {
          console.error(e);
          setJob(null);
        }
      }
    })();
    return () => ac.abort();
  }, [id]);

  const mapped = useMemo(() => {
    if (!job) return null;
    return {
      title: job.title || "Untitled Role",
      company: job.company || "",
      html: buildJobHtml(job),
      id: job.id || job.slug || id,
    };
  }, [job, id]);

  // --- Apply form (wired to backend) ---
  const [form, setForm] = useState({ name: "", address: "", email: "", cv: null });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // toast state
  const [toast, setToast] = useState({ show: false, variant: "success", title: "", message: "" });
  const showToast = (variant, title, message = "") => setToast({ show: true, variant, title, message });
  const hideToast = () => setToast((t) => ({ ...t, show: false }));

  const onChange = (e) => {
    const { name, value, files } = e.target;
    setForm((f) => ({ ...f, [name]: files ? files[0] : value }));
  };

  const validate = () => {
    const next = {};
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!form.name.trim()) next.name = "Name is required.";
    if (!form.address.trim()) next.address = "Address is required.";
    if (!form.email.trim()) next.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email.";
    if (!form.cv) next.cv = "Please upload your CV (PDF or DOC).";
    else {
      if (!allowed.includes(form.cv.type)) next.cv = "Allowed types: PDF, DOC, DOCX.";
      if (form.cv.size > 5 * 1024 * 1024) next.cv = "File too large (max 5 MB).";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast("error", "Missing or invalid fields", "Please review the highlighted fields.");
      return;
    }

    try {
      setLoading(true);

      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("address", form.address);
      fd.append("cv", form.cv);
      fd.append("jobId", mapped?.id || id);
      fd.append("jobTitle", mapped?.title || "");

      const res = await fetch(`${API}/api/careers/apply`, {
        method: "POST",
        body: fd, // no manual Content-Type
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to submit application");

      showToast("success", "Application submitted!", "Thanks — we’ll get back to you shortly.");
      setForm({ name: "", address: "", email: "", cv: null });
      setErrors({});
    } catch (err) {
      console.error(err);
      showToast("error", "Submission failed", err.message || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!mapped) {
    return (
      <div className="container py-5">
        <h3>Role not found</h3>
        <p className="text-muted">This job may have been moved or closed.</p>
        <Link to="/careers" className="btn btn-outline-secondary">← Back to Careers</Link>
      </div>
    );
  }

  const { title, company, html } = mapped;

  return (
    <div className="avena-jobdetail">
      {/* Floating Toast Container */}
      <div className="avena-toastContainer">
        <Toast
          show={toast.show}
          variant={toast.variant}
          title={toast.title}
          message={toast.message}
          onClose={hideToast}
          duration={5000}
        />
      </div>

      <PageBanner
        title={title}
        bg={aboutContact}
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Careers", href: "/careers" },
          { label: title },
        ]}
      />

      <section className="avena-jobdetail__body">
        <div className="container">
          <div className="row g-4">
            {/* LEFT: Job content */}
            <div className="col-12 col-lg-8">
              <div className="avena-jobdetail__mast mb-3">
                <div>
                  {company && <div className="avena-jobdetail__company">{company}</div>}
                  <h1 className="avena-jobdetail__title">{title}</h1>
                </div>
              </div>

              <div className="pd-rich" dangerouslySetInnerHTML={{ __html: html }} />
            </div>

            {/* RIGHT: Apply Form */}
            <div className="col-12 col-lg-4">
              <aside className="avena-jobdetail__side">
                <div className="apply-card">
                  <h4 className="apply-card__title">Apply For Role</h4>

                  {/* Inline alerts replaced by toasts; keeping field-level errors below inputs */}

                  <form onSubmit={onSubmit} noValidate>
                    <div className="apply-field">
                      <label className="apply-label">Applicant’s Name</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        className={`apply-input ${errors.name ? "is-invalid" : ""}`}
                        required
                        disabled={loading}
                      />
                      {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                    </div>

                    <div className="apply-field">
                      <label className="apply-label">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={onChange}
                        className={`apply-input ${errors.email ? "is-invalid" : ""}`}
                        required
                        disabled={loading}
                      />
                      {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                    </div>

                    <div className="apply-field">
                      <label className="apply-label">Contact Address</label>
                      <textarea
                        name="address"
                        value={form.address}
                        onChange={onChange}
                        className={`apply-textarea ${errors.address ? "is-invalid" : ""}`}
                        rows={1}
                        required
                        disabled={loading}
                      />
                      {errors.address && <div className="invalid-feedback d-block">{errors.address}</div>}
                    </div>

                    <div className="apply-field">
                      <label className="apply-label">CV / Resume</label>
                      <input
                        type="file"
                        name="cv"
                        accept=".pdf,.doc,.docx"
                        onChange={onChange}
                        className={`apply-input file-input ${errors.cv ? "is-invalid" : ""}`}
                        required
                        disabled={loading}
                      />
                      <small className="apply-hint">Accepted formats: PDF, DOC, DOCX. Max 5 MB.</small>
                      {errors.cv && <div className="invalid-feedback d-block">{errors.cv}</div>}
                      {form.cv && !errors.cv && (
                        <div className="apply-file mt-1">
                          <span className="apply-file__name">{form.cv.name}</span>
                          <span className="apply-file__size">{(form.cv.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                      )}
                    </div>

                    <button type="submit" className="apply-btn" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner" aria-hidden="true" /> Submitting…
                        </>
                      ) : (
                        "Submit Now"
                      )}
                    </button>
                  </form>
                </div>

                <Link to="/careers" className="career-backlink mt-3">← Back to Careers</Link>
              </aside>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
